import React, { useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import { useIntl } from 'react-intl';
import { TranslationUtils } from '../../utils';
import { PaymentFormValues } from './PaymentForm';
import { getCountriesConfig, getCountryLocalityConfig, LocalityConfig } from './addressFields.conf';

interface AddressFieldsProps {
    initialCountry: string;
    formikProps: FormikProps<PaymentFormValues>;
    onFieldsChange: (localityConfig: LocalityConfig[], formikProps: FormikProps<PaymentFormValues>) => any;
}

export const AddressFields: React.FC<AddressFieldsProps> = (props) => {
    const { locale } = useIntl();
    const countriesConfig = getCountriesConfig(locale);
    const { initialCountry, formikProps, onFieldsChange } = props;
    const { values, errors, touched, handleChange, handleBlur } = formikProps;
    const [selectedCountry, setSelectedCountry] = useState(initialCountry);
    const [selectedCountryConfig, setSelectedCountryConfig] = useState(getCountryLocalityConfig(selectedCountry, locale));

    useEffect(() => {
        const localityConfig = getCountryLocalityConfig(selectedCountry, locale);
        setSelectedCountryConfig(localityConfig);
    }, [locale, selectedCountry]);

    useEffect(() => {
        onFieldsChange(selectedCountryConfig, formikProps);
    }, [selectedCountryConfig]);

    const handleOnCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCountry = e.target.value;
        formikProps.setFieldValue('country', selectedCountry);
        setSelectedCountry(selectedCountry);
    };

    const renderLocalityField = (localityConfig: LocalityConfig) => {
        let control: JSX.Element;
        const id = localityConfig.storeKey;
        const label = localityConfig.fieldConfig.label;

        const common = {
            id,
            onChange: handleChange,
            onBlur: handleBlur,
            values: values.state,
            className: `body-regular w-100 form-control ${errors[id] && touched[id] ? 'invalid' : ''}`,
        };

        if ('options' in localityConfig.fieldConfig) {
            control = (
                <select {...common}>
                    {localityConfig.fieldConfig.options.map((o, i) => {
                        const key = Object.keys(o)[0];
                        const value = o[key];
                        return (
                            <React.Fragment>
                                <option key={key} value={value} disabled={i === 0} selected={i === 0}>
                                    {value}
                                </option>
                            </React.Fragment>
                        );
                    })}
                </select>
            );
        } else {
            control = <input {...common} value={formikProps.values[id]} />;
        }

        return (
            <div className="form-row mt-16">
                <div className="body-regular col-12">
                    <label className="body-regular col-form-label type-body" htmlFor="">
                        {label}
                    </label>
                    {control}
                </div>
            </div>
        );
    };

    return (
        <div className="payment-form-address-fields">
            <div className="form-row mt-16">
                <div className="col-12">
                    <label className="body-regular col-form-label type-body" htmlFor="">
                        {TranslationUtils.get('payment_form_country')}
                    </label>
                    <select
                        onChange={handleOnCountryChange}
                        onBlur={handleBlur}
                        value={values.country}
                        name="country"
                        aria-required="true"
                        id="country"
                        className={`body-regular w-100 form-control ${errors.country && touched.country ? 'invalid' : ''}`}
                    >
                        {countriesConfig.options.map((country) => {
                            return (
                                <option key={country.iso} value={country.iso} selected>
                                    {country.label}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>

            <div className="form-row mt-16">
                <div className="col-12">
                    <label className="body-regular col-form-label type-body" htmlFor="">
                        {TranslationUtils.get('billing_address')}
                    </label>
                    <input
                        id="billing_address"
                        aria-label="billing_address"
                        aria-required="true"
                        className={`body-regular w-100 form-control ${errors.billing_address && touched.billing_address ? 'invalid' : ''}`}
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.billing_address}
                        name="billing_address"
                    />
                </div>
            </div>
            {selectedCountryConfig.map(renderLocalityField)}
        </div>
    );
};
